import { Box, NumberInput } from '@chakra-ui/react';
import isNil from 'lodash/isNil';
import toString from 'lodash/toString';
import React, {
  FunctionComponent as FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  decimalRegex,
  decimalWithNegativeRegex,
  intNumRegex,
  intNumWithNegativeRegex,
} from '../../constants/common';

/** Characters that no value accepted by the regexes below can contain. */
const NON_NUMERIC_CHARACTERS = /[^\d.-]/g;

export interface MCustomNumberInputProps extends Omit<
  NumberInput.RootProps,
  'onChange' | 'inputMode' | 'value'
> {
  /** `data-testid` is allowed here so call sites can override the default id when several number inputs share a screen. */
  styleProps?: NumberInput.InputProps & { 'data-testid'?: string };
  handleChange?: (val: string) => true | false;
  LeftElement?: null | React.ReactElement;
  RightElement?: null | React.ReactElement;
  ref?: any;
  inputMode?: React.InputHTMLAttributes<any>['inputMode'];
  precision?: number;
  placeholder?: string;
  // v2 shipped `isReadOnly`/`isDisabled`; v3's real `NumberInput.Root` only
  // understands `readOnly`/`disabled`. Real names still work directly and
  // take precedence if a caller somehow passes both.
  isReadOnly?: boolean;
  isDisabled?: boolean;
  // `NumberInput.RootProps`'s own `onChange` is the inherited native
  // `<div onChange>` (1 event arg) — this component's real `onChange` is a
  // v2-style `(valueAsString, valueAsNumber)` callback (see
  // `handleInternalChange` below), which every real consumer already relies
  // on. Redeclared here (`Omit` above drops the native one) so those
  // consumers type-check instead of getting silently matched against the
  // wrong signature.
  onChange?: (valueAsString: string, valueAsNumber: number) => void;
  // The real `NumberInput.RootProps['value']` is `string` only (zag-js's
  // NumberInput machine always stores its value as a string internally),
  // but this component already coerces via lodash `toString()` before ever
  // touching internal state or the real `NumberInput.Root`'s own `value`
  // (see below) — every real consumer relies on passing a plain `number`
  // (form field values, computed totals, etc.), which already worked at
  // runtime and only failed to type-check.
  value?: string | number;
  /**
   * Opt-in: defers committing a cleared field to `onChange` until blur (instead
   * of on every keystroke), and resets it to `min` (or 0) at blur if still
   * empty. Off by default so existing consumers are unaffected.
   */
  resetEmptyValueOnBlur?: boolean;
}

export const MCustomNumberInput: FC<MCustomNumberInputProps> = React.forwardRef<
  any,
  MCustomNumberInputProps
>(
  (
    {
      // non-standard props
      styleProps = {},
      handleChange = () => true,
      resetEmptyValueOnBlur = false,
      // standard number props
      onChange,
      onBlur,
      inputMode,
      placeholder,
      value,
      min = 0,
      max = Infinity,
      LeftElement = null,
      RightElement = null,
      precision,
      isReadOnly,
      isDisabled,
      readOnly,
      disabled,
      // v2's NumberInput forwarded a root-level `aria-label` to its input; v3's
      // `NumberInput.Root` leaves it on the wrapper div, so the spinbutton
      // loses its accessible name. `styleProps['aria-label']` still wins.
      'aria-label': ariaLabel,
      ...rest
    }: MCustomNumberInputProps,
    reference: any,
  ) => {
    const [internalValue, setInternalValue] = useState<string>(toString(value));
    const inputRef = useRef<HTMLInputElement>(null);
    const isDecimal = () => inputMode === 'decimal';
    // v2's `precision` rounded and zero-padded the value to that many decimals
    // on blur (e.g. "1.5" -> "1.50"). v3 has no `precision`; its equivalent is
    // `formatOptions`, which zag applies on blur. `useGrouping: false` keeps
    // v2's plain `toFixed` output (no thousands separators).
    const formatOptions = useMemo<Intl.NumberFormatOptions | undefined>(
      () =>
        isNil(precision)
          ? undefined
          : {
              minimumFractionDigits: precision,
              maximumFractionDigits: precision,
              useGrouping: false,
            },
      [precision],
    );

    /*
     * Call sites that group a value at rest and ungroup it while editing (the
     * rate amount) swap `formatOptions` on focus. zag rewrites the text a
     * frame later and maps the old selection onto the new text by matching
     * prefixes and suffixes, so "all of 1,000.00" becomes "000.00" of
     * "1000.00" — typing over a tabbed-into or Playwright-filled field keeps
     * the leading digit (20 -> 120).
     *
     * The focus handler's frame callback reads the selection once the browser
     * has settled it (Tab selects all after the focus event). The format
     * effect's callback is queued after zag's own (zag's watch is an effect in
     * a child, which runs first) and re-selects the rewritten text in that
     * same frame. Matching frame timestamps keeps a later format change from
     * acting on a stale reading.
     */
    const selectAllFrameRef = useRef<number | null>(null);
    const formatOptionsKey = JSON.stringify(rest.formatOptions);

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      requestAnimationFrame((timestamp) => {
        const isAllSelected =
          input.value !== '' &&
          input.selectionStart === 0 &&
          input.selectionEnd === input.value.length;
        selectAllFrameRef.current = isAllSelected ? timestamp : null;
      });
      styleProps.onFocus?.(event);
    };

    useEffect(() => {
      const input = inputRef.current;
      if (!input || input.ownerDocument.activeElement !== input) {
        return;
      }
      const frame = requestAnimationFrame((timestamp) => {
        if (selectAllFrameRef.current === timestamp) {
          input.select();
        }
      });
      return () => cancelAnimationFrame(frame);
    }, [formatOptionsKey]);

    useEffect(() => {
      const valueStr = toString(value);
      if (
        valueStr === internalValue ||
        internalValue === '-' ||
        (internalValue === '' && resetEmptyValueOnBlur)
      ) {
        return;
      }
      // Don't clobber an in-progress string that already parses to the same
      // number — preserves trailing "." ("200.") and trailing zeros ("100.50")
      // while the user is still typing decimals.
      if (internalValue !== '' && Number(internalValue) === Number(valueStr)) {
        return;
      }
      setInternalValue(valueStr);
    }, [value, internalValue, resetEmptyValueOnBlur]);

    // v2's input was React-controlled, so a keystroke the regex below rejects
    // vanished at once. zag's input is uncontrolled and only re-syncs when
    // `value` changes, which a rejection doesn't do — so put the accepted text
    // back by hand, or "2.5" stays on screen while the value is 2.
    const revertRejectedKeystroke = () => {
      if (inputRef.current && inputRef.current.value !== internalValue) {
        inputRef.current.value = internalValue;
      }
    };

    const handleInternalChange = ({
      value: rawValue,
    }: NumberInput.ValueChangeDetails) => {
      const val = rawValue.trim();
      // this is for external onChange overwrites. handleChange returns true/false
      if (handleChange && handleChange(val)) {
        let regex = isDecimal() ? decimalRegex : intNumRegex;
        if (isNil(min) || min < 0) {
          regex = isDecimal()
            ? decimalWithNegativeRegex
            : intNumWithNegativeRegex;
          // Allow user to enter negative sign, but don't emit to parent yet since it is not a valid number at this point
          if (val === '-') {
            setInternalValue(val);
            return;
          }
        }
        // test new input value against regex. use old value if it does not pass.
        const isAccepted = regex.test(String(val));
        let newStringValue = isAccepted ? String(val) : internalValue;
        // allow user to clear input
        if (val === '') {
          newStringValue = '';
        } else if (val === '.' && isDecimal()) {
          newStringValue = '0.';
        } else if (!isAccepted) {
          revertRejectedKeystroke();
        }
        // keep internal as a string. NumberInput handles decimals better when casting to a string.
        setInternalValue(newStringValue);
        // Defer notifying the parent while mid-clear; onChange fires once the
        // user types a real value, or at blur (see handleBlur below).
        if (newStringValue === '' && resetEmptyValueOnBlur) {
          return;
        }
        // form validation is expecting an integer
        onChange && onChange(newStringValue, Number(newStringValue));
      }
    };

    /*
     * v2 dropped every character a number can't contain from a change, so
     * pasting "9ab" or "1,000" kept 9 or 1000. zag checks pasted text whole
     * and refuses all of it. Strip those characters (the field's own thousands
     * separators included) and validate the result like a keystroke. A paste
     * with nothing numeric in it changes nothing, as typing letters doesn't.
     */
    const handleInputPaste = (
      event: React.ClipboardEvent<HTMLInputElement>,
    ) => {
      styleProps.onPaste?.(event);
      const pasted = event.clipboardData.getData('text');
      const numericPaste = pasted.replace(NON_NUMERIC_CHARACTERS, '');
      if (event.defaultPrevented || numericPaste === pasted) {
        return;
      }
      event.preventDefault();
      if (numericPaste === '') {
        return;
      }
      const { value: text, selectionStart, selectionEnd } = event.currentTarget;
      const nextValue = (
        text.slice(0, selectionStart ?? text.length) +
        pasted +
        text.slice(selectionEnd ?? text.length)
      ).replace(NON_NUMERIC_CHARACTERS, '');
      handleInternalChange({
        value: nextValue,
        valueAsNumber: Number(nextValue),
      });
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (resetEmptyValueOnBlur && internalValue === '') {
        const defaultValue = String(min || 0);
        setInternalValue(defaultValue);
        onChange && onChange(defaultValue, Number(defaultValue));
      }
      onBlur && onBlur(event);
    };

    return (
      <NumberInput.Root
        ref={reference}
        formatOptions={formatOptions}
        onValueChange={handleInternalChange}
        onBlur={handleBlur}
        value={internalValue}
        min={min}
        max={max}
        readOnly={readOnly ?? isReadOnly}
        disabled={disabled ?? isDisabled}
        w="100%"
        {...rest}
      >
        {/* Flex, like v3's `InputGroup`: v3's `InputElement` sets no `top`, so
            in a block wrapper the end element drops to its static position
            below the input. */}
        <Box position="relative" display="flex" w="100%">
          {LeftElement}
          <NumberInput.Input
            ref={inputRef}
            data-testid="custom-number-input"
            pattern="(-)?[0-9]+(.[0-9]+)?"
            // overwrite default NumberInputField padding
            paddingInlineEnd={2}
            paddingInlineStart={2}
            placeholder={placeholder}
            aria-label={ariaLabel}
            {...styleProps}
            onFocus={handleInputFocus}
            onPaste={handleInputPaste}
          />
          {RightElement}
        </Box>
      </NumberInput.Root>
    );
  },
);
